import { useQuery, useMutation } from 'react-query'
import client, { deleteclient, patchclient } from './api-client';

const parcelQueryConfig = {
  staleTime: 1000 * 60 * 60,
  cacheTime: 1000 * 60 * 60,
}

async function fetchParcels(user) {
  const data = await client(`parcels/?client_id=${user.id}`)
  return data
}

export function useParcels(user) {
  return useQuery(['parcels', user.username], () => fetchParcels(user), {
    refetchAllOnWindowFocus: false,
    ...parcelQueryConfig
  });
}

async function fetchParcelDetails(parcel_id) {
  const data = await client(`parcelsdetail/${parcel_id}/`)
  return data
}

export function useParcelDetail(parcel_id) {
  return useQuery(['parcel', parcel_id], () => fetchParcelDetails(parcel_id), {
    refetchAllOnWindowFocus: false,
    enabled: false,
    ...parcelQueryConfig
  });
}


async function fetchSmallParcels(user) {
  const data = await client(`smallparcels/?client_id=${user.id}`)
  return data
}

export function useSmallParcels(user) {
  return useQuery(['smallparcels', user.id], () => fetchSmallParcels(user), {
    refetchAllOnWindowFocus: false,
    ...parcelQueryConfig
  });
}


async function updateParcel(parcel) {
  const data = await patchclient(`parcels/${parcel.id}/`, { body: parcel })
  return data;
}

export function useUpdateParcelMutation(parcel) {
  return useMutation((parcel) => updateParcel(parcel));
}

async function fetchParcelsTotalHectares(user) {
  const data = await client(`parcels/total_hectares/?client_id=${user.id}`)
  return data
}

export function useParcelsTotalHectares(user) {
  return useQuery(['parcels_total_hectares', user.username], () => fetchParcelsTotalHectares(user), {
    refetchAllOnWindowFocus: false,
    enabled: false,
    ...parcelQueryConfig
  });
}

async function fetchParcelsResolutionsDates(user, parcel, layer, fromDate) {
  let data = []
  
  if (!['10x10 meters', '3x3 meters'].includes(layer.layer_resolution.resolution)){
    data = await client(`parcels/${parcel.id}/resolutions/${layer.layer_resolution.key}/dates?client_id=${user.id}`)
    return data?.data
  } else {
    const today = new Date().toISOString().split('T')[0]; // Get today's date in 'YYYY-MM-DD' format
    data = await client(
      `parcels/${parcel.id}/layers/${layer.key}/statistics/?from_date=${fromDate}&to_date=${today}&maxcc=1`
      //`catalog/parcel_fis_history/?parcel_id=${parcel.id}&layer=${layer.layer_stats}&from=${fromDate}&to=${today}`
    )
    return data?.data
  }
}

export function useParcelsResolutionsDates(user, parcel, layer, fromDate, options = {}) {
  return useQuery([
    'parcels_resolutions_dates',
    user.username,
    parcel.id,
    layer.layer_resolution.key,
    layer.key,
    fromDate
  ], () => fetchParcelsResolutionsDates(user, parcel, layer, fromDate), {
    refetchAllOnWindowFocus: false,
    enabled: (!!user && !!parcel && !!layer && !!fromDate),
    onError: (error) => {
      // Log specific parcel size limit errors for debugging
      if (error?.error_code === 'PARCEL_SIZE_EXCEEDS_LIMIT') {
        console.warn('Parcel size exceeds limit:', error);
      }
    },
    ...parcelQueryConfig,
    ...options,
  });
}

async function fetchParcelsLayersStatisticsDate(user, parcel, layer, date) {
  const data = await client(`parcels/${parcel.id}/layers/${layer.key}/statistics-date?client_id=${user.id}&date=${date}`)
  return data
}

export function useParcelsLayersStatisticsDate(user, parcel, layer, date) {
  return useQuery([
    'parcels_layers_statistics_date', 
    user.username, 
    parcel.id, 
    layer.key,
    date
  ], () => fetchParcelsLayersStatisticsDate(user, parcel, layer, date), {
    refetchAllOnWindowFocus: false,
    enabled: true,
    ...parcelQueryConfig
  });
}



async function fetchParcelsMultipleResolutionsDates(user, parcel, resolution, fromDate) {
  let data = []
  
  data = await client(`parcels/${parcel?.id}/resolutions/${resolution?.key}/dates?from_date=${fromDate}&client_id=${user.id}`)
  return data?.data
}

export function useParcelsMultipleResolutionsDates(user, parcel, resolution, fromDate) {
  return useQuery([
    'parcels_resolutions_dates', 
    user.username, 
    parcel?.id, 
    resolution?.key,
    fromDate
  ], () => fetchParcelsMultipleResolutionsDates(user, parcel, resolution, fromDate), {
    refetchAllOnWindowFocus: false,
    enabled: (!!user && !!parcel && !!resolution && !!fromDate),
    ...parcelQueryConfig
  });
}

async function fetchDeforestationExplanation(parcelId) {
  const data = await client(`parcels/${parcelId}/explain-deforestation/`);
  return data;
}

export function useDeforestationExplanation(parcelId, auditVersion) {
  return useQuery(
    ['deforestation-explanation', parcelId, auditVersion],
    () => fetchDeforestationExplanation(parcelId),
    { 
      enabled: !!parcelId,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 60,
    }
  );
}
